import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { QRCodeCanvas } from "qrcode.react";
import { toast } from "sonner";
import { ChevronLeft, Check, Download, CreditCard, QrCode, Repeat, Star, Copy, Loader2, Calendar } from "lucide-react";
import dayjs from "dayjs";
import { api, fileUrl, invoiceUrl } from "@/lib/api";
import { money, fmtDateTime, fmtSlotDate, itemLine } from "@/lib/format";
import { flowFor, orderKind, statusLabel, statusComment } from "@/lib/status";
import { useAuth } from "@/context/AuthContext";
import { useCustomerProfile } from "@/hooks/useLocal";
import { useShop } from "@/context/ShopContext";
import { StatusBadge } from "@/components/common/StatusBadge";
import { Spinner, ErrorState } from "@/components/common/States";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { cn } from "@/lib/utils";

export default function OrderDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { profile } = useCustomerProfile();
  const { shop } = useShop();
  const [dialog, setDialog] = useState(null); // cancel | upi
  const [paying, setPaying] = useState(false);
  const [stars, setStars] = useState(0);
  const [note, setNote] = useState("");

  const query = useQuery({ queryKey: ["booking", id], queryFn: () => api.getBooking(id), refetchInterval: 15000, retry: 2 });
  const servicesQuery = useQuery({ queryKey: ["services"], queryFn: api.getServices });

  const booking = query.data;
  const flow = booking ? flowFor(booking.mode) : [];
  const currentIdx = booking ? flow.indexOf(booking.status) : -1;
  const historyMap = new Map((booking?.status_history ?? []).map((h) => [h.status, h.at]));
  const categoryOf = (sid) => servicesQuery.data?.find((s) => s.service_id === sid)?.category;
  const kind = booking ? orderKind(booking.items, categoryOf) : "laundry";
  const phone = user?.phone || profile?.phone || booking?.phone || "";

  const afterChange = (b, msg) => {
    queryClient.setQueryData(["booking", id], b);
    queryClient.invalidateQueries({ queryKey: ["bookings"] });
    setDialog(null);
    toast.success(msg);
  };

  const submitRating = useMutation({
    mutationFn: () => api.rateBooking(id, phone, stars, note.trim()),
    onSuccess: (b) => afterChange(b, "Thanks for your feedback!"),
    onError: (e) => toast.error(e.message || "Could not submit rating"),
  });

  if (query.isPending) return <div className="mx-auto max-w-2xl"><Spinner /></div>;
  if (query.isError || !booking) return <div className="mx-auto max-w-2xl"><ErrorState message="Couldn't load this order." onRetry={() => query.refetch()} /></div>;

  // Web cancel is only offered while the order still awaits the shop's confirmation. Once confirmed,
  // it's locked (the shared backend cancel rule is unchanged, so mobile is unaffected).
  const canCancel = (booking.status === "placed" || booking.status === "pending_approval") && booking.slot_date >= dayjs().format("YYYY-MM-DD");
  const showLockedNote = booking.status === "confirmed";
  const canPayOnline = booking.bill_status === "final" && booking.total > 0 && booking.payment_status !== "paid" && booking.payment_status !== "refund_due" && booking.status !== "cancelled" && booking.status !== "completed";
  const upiIds = shop?.upi_ids?.length ? shop.upi_ids : shop?.upi_id ? [shop.upi_id] : [];
  const primaryUpiId = shop?.upi_id || upiIds[0] || "";
  const upiLink = primaryUpiId ? `upi://pay?pa=${encodeURIComponent(primaryUpiId)}&pn=${encodeURIComponent(shop?.upi_name || "Goldys")}&am=${booking.total}&cu=INR&tn=${encodeURIComponent(booking.code)}` : "";

  const cancel = () => api.cancelBooking(id, phone).then((b) => afterChange(b, `${b.code} cancelled`)).catch((e) => toast.error(e.message || "Could not cancel"));

  const payOnline = async () => {
    setPaying(true);
    try {
      const { url } = await api.createCheckout(booking.id, phone, window.location.origin);
      window.location.assign(url);
    } catch (e) {
      toast.error(e.message || "Could not start payment");
      setPaying(false);
    }
  };

  const discountRows = [
    booking.promo_discount > 0 && [`Promo ${booking.promo_code}`, booking.promo_discount],
    booking.earlybird_discount > 0 && ["Early-bird", booking.earlybird_discount],
    booking.points_discount > 0 && ["Loyalty points", booking.points_discount],
    booking.game_points_discount > 0 && ["Game points", booking.game_points_discount],
    booking.milestone_points_discount > 0 && ["Milestone bonus", booking.milestone_points_discount],
    booking.reward_discount > 0 && ["Free wash reward", booking.reward_discount],
    booking.welcome_discount > 0 && ["Welcome offer", booking.welcome_discount],
    booking.free_wash_discount > 0 && ["Free-kg allowance", booking.free_wash_discount],
  ].filter(Boolean);

  return (
    <div className="mx-auto max-w-2xl space-y-5 py-4">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <button onClick={() => navigate(-1)} className="flex h-10 w-10 items-center justify-center rounded-full hover:bg-secondary"><ChevronLeft className="h-6 w-6" /></button>
          <div>
            <h1 className="font-display text-xl font-bold">{booking.code}</h1>
            <p className="text-xs text-muted-foreground">{booking.mode === "pickup" ? "Doorstep pickup & delivery" : "In-store drop-off"}</p>
          </div>
        </div>
        <StatusBadge status={booking.status} mode={booking.mode} kind={kind} />
      </div>

      <p className="rounded-xl bg-secondary px-4 py-3 text-sm">{statusComment(booking.status, kind)}</p>

      {/* Timeline */}
      {booking.status !== "cancelled" ? (
        <div className="rounded-2xl border border-border p-5">
          <h2 className="mb-4 font-display text-sm font-bold">Order progress</h2>
          <ol className="space-y-0">
            {flow.map((st, i) => {
              const done = i <= currentIdx;
              const at = historyMap.get(st);
              return (
                <li key={st} className="flex gap-3">
                  <div className="flex flex-col items-center">
                    <div className={cn("flex h-7 w-7 items-center justify-center rounded-full border-2", done ? "border-primary bg-primary text-primary-foreground" : "border-border bg-card text-transparent")}>
                      {done ? <Check className="h-4 w-4" /> : <span className="h-1.5 w-1.5 rounded-full bg-border" />}
                    </div>
                    {i < flow.length - 1 ? <div className={cn("w-0.5 flex-1 min-h-[22px]", i < currentIdx ? "bg-primary" : "bg-border")} /> : null}
                  </div>
                  <div className="pb-4">
                    <p className={cn("text-sm font-medium", done ? "text-foreground" : "text-muted-foreground")}>{statusLabel(st, booking.mode, kind)}</p>
                    {at ? <p className="text-xs text-muted-foreground">{fmtDateTime(at)}</p> : null}
                  </div>
                </li>
              );
            })}
          </ol>
          {booking.expected_delivery_date ? <p className="mt-2 text-xs text-muted-foreground" data-testid="expected-delivery">Expected delivery: {fmtSlotDate(booking.expected_delivery_date)}</p> : <p className="mt-2 text-xs text-muted-foreground" data-testid="expected-delivery">Expected delivery: to be confirmed</p>}
        </div>
      ) : null}

      {/* Slot */}
      <div className="rounded-2xl border border-border p-5 text-sm">
        <div className="flex items-center gap-2"><Calendar className="h-4 w-4 text-primary" /> {fmtSlotDate(booking.slot_date)} · {booking.slot_time}</div>
        {booking.address ? <p className="mt-2 text-muted-foreground">{booking.address}</p> : null}
      </div>

      {/* Photos */}
      {booking.photos?.length ? (
        <div className="flex gap-2 overflow-x-auto no-scrollbar">
          {booking.photos.map((p, i) => <img key={i} src={fileUrl(p)} alt="garment" className="h-24 w-24 flex-shrink-0 rounded-xl border border-border object-cover" />)}
        </div>
      ) : null}

      {/* Receipt */}
      <div className="rounded-2xl bg-secondary p-5">
        <div className="flex items-center justify-between">
          <h2 className="font-display text-sm font-bold">Receipt</h2>
          <span className="rounded-full bg-card px-2.5 py-1 text-xs font-medium">{booking.bill_status === "final" ? "Final bill" : "Estimated"}</span>
        </div>
        <div className="mt-3 space-y-1.5 text-sm">
          {booking.items.map((it, i) => (
            <div key={i} className="flex justify-between"><span className="text-foreground/80">{itemLine(it, booking.hide_quantities)}</span><span>{money(it.price * it.qty)}</span></div>
          ))}
          {(booking.extra_charges ?? []).map((c, i) => (
            <div key={`x${i}`} className="flex justify-between"><span className="text-foreground/80">{c.label}</span><span>{money(c.amount)}</span></div>
          ))}
          <div className="flex justify-between border-t border-border pt-1.5"><span className="text-foreground/70">Subtotal</span><span>{money(booking.subtotal)}</span></div>
          {discountRows.map(([l, a], i) => <div key={i} className="flex justify-between text-[hsl(var(--success))]"><span>{l}</span><span>- {money(a)}</span></div>)}
          {booking.delivery_fee > 0 ? <div className="flex justify-between"><span className="text-foreground/70">Pickup & delivery</span><span>{money(booking.delivery_fee)}</span></div> : null}
          {booking.express_fee > 0 ? <div className="flex justify-between"><span className="text-foreground/70">Express fee</span><span>{money(booking.express_fee)}</span></div> : null}
          {booking.slot_surcharge > 0 ? <div className="flex justify-between"><span className="text-foreground/70">Slot surcharge</span><span>{money(booking.slot_surcharge)}</span></div> : null}
          <div className="flex justify-between border-t border-border pt-1.5 font-display text-base font-bold"><span>Total</span><span>{money(booking.total)}</span></div>
          {booking.points_earned > 0 ? <p className="text-xs text-muted-foreground">You&apos;ll earn {booking.points_earned} points{booking.points_pending ? " (after completion)" : ""}.</p> : null}
        </div>
        {booking.bill_status === "final" ? (
          <a href={invoiceUrl(booking.id, phone)} target="_blank" rel="noreferrer" data-testid="download-invoice" className="mt-3 inline-flex items-center gap-1 text-sm font-semibold text-primary"><Download className="h-4 w-4" /> Download bill (PDF)</a>
        ) : null}
      </div>

      {/* Payment status */}
      <div className="flex flex-wrap gap-2">
        {canPayOnline ? (
          <button onClick={payOnline} disabled={paying} data-testid="order-pay-online" className="inline-flex items-center gap-2 rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground disabled:opacity-60">
            {paying ? <Loader2 className="h-4 w-4 animate-spin" /> : <CreditCard className="h-4 w-4" />} Pay {money(booking.total)} online
          </button>
        ) : null}
        {canPayOnline && upiLink ? (
          <button onClick={() => setDialog("upi")} data-testid="order-pay-upi" className="inline-flex items-center gap-2 rounded-full border border-primary px-5 py-2.5 text-sm font-semibold text-primary"><QrCode className="h-4 w-4" /> Pay via UPI</button>
        ) : null}
        {booking.payment_status === "paid" ? <span className="inline-flex items-center gap-1 rounded-full bg-[hsl(var(--success)/0.14)] px-4 py-2 text-sm font-semibold text-[hsl(142_60%_32%)]"><Check className="h-4 w-4" /> Paid</span> : null}
      </div>

      {/* Actions */}
      <div className="flex flex-wrap gap-2">
        {canCancel ? <button onClick={() => setDialog("cancel")} data-testid="order-cancel" className="rounded-full border border-destructive px-4 py-2 text-sm font-semibold text-destructive">Cancel</button> : null}
        {(booking.status === "completed" || booking.status === "cancelled") ? <button onClick={() => navigate(`/booking?reorder=${booking.id}`)} className="inline-flex items-center gap-1 rounded-full bg-[hsl(var(--brand-tertiary))] px-4 py-2 text-sm font-semibold text-primary"><Repeat className="h-4 w-4" /> Reorder</button> : null}
      </div>
      {showLockedNote ? (
        <p className="rounded-xl bg-secondary px-4 py-3 text-sm text-muted-foreground" data-testid="order-locked-note">
          This order is confirmed and now being prepared, so it can no longer be cancelled here. Need to make a change? Please call the shop.
        </p>
      ) : null}

      {/* Rating */}
      {booking.status === "completed" ? (
        <div className="rounded-2xl border border-border p-5">
          <h2 className="font-display text-sm font-bold">{booking.rating ? "Your rating" : "Rate this order"}</h2>
          <div className="mt-2 flex gap-1">
            {[1, 2, 3, 4, 5].map((n) => (
              <button key={n} disabled={!!booking.rating} onClick={() => setStars(n)} data-testid={`rating-star-${n}`}>
                <Star className={cn("h-7 w-7", (booking.rating || stars) >= n ? "fill-current text-[hsl(var(--warning))]" : "text-border")} />
              </button>
            ))}
          </div>
          {!booking.rating ? (
            <>
              <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} placeholder="Tell us more (optional)" className="mt-3 w-full rounded-xl border border-border bg-card p-3 text-sm outline-none focus:border-primary" />
              <button disabled={!stars || submitRating.isPending} onClick={() => submitRating.mutate()} data-testid="submit-rating" className="mt-2 rounded-full bg-primary px-5 py-2 text-sm font-semibold text-primary-foreground disabled:opacity-50">Submit</button>
            </>
          ) : booking.rating_note ? <p className="mt-2 text-sm text-muted-foreground">&ldquo;{booking.rating_note}&rdquo;</p> : null}
        </div>
      ) : null}

      {/* Cancel dialog */}
      <Dialog open={dialog === "cancel"} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Cancel this order?</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">This can&apos;t be undone. Repeated no-shows may affect your account.</p>
          <div className="mt-2 flex gap-2">
            <button onClick={() => setDialog(null)} className="flex-1 rounded-full border border-border py-2.5 text-sm font-semibold">Keep order</button>
            <button onClick={cancel} data-testid="confirm-cancel" className="flex-1 rounded-full bg-destructive py-2.5 text-sm font-semibold text-destructive-foreground">Cancel order</button>
          </div>
        </DialogContent>
      </Dialog>

      {/* UPI dialog */}
      <Dialog open={dialog === "upi"} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Pay via UPI</DialogTitle></DialogHeader>
          <div className="flex flex-col items-center gap-3">
            {shop?.upi_qr_path ? (
              <img src={fileUrl(shop.upi_qr_path)} alt="UPI QR" className="h-52 w-52 rounded-xl border border-border object-contain" />
            ) : upiLink ? (
              <div className="rounded-xl border border-border bg-white p-3"><QRCodeCanvas value={upiLink} size={200} /></div>
            ) : null}
            <p className="text-sm font-semibold">{money(booking.total)}</p>
            {primaryUpiId ? (
              <button onClick={() => { navigator.clipboard?.writeText(primaryUpiId); toast.success("UPI ID copied"); }} className="inline-flex items-center gap-1 text-sm text-primary"><Copy className="h-4 w-4" /> {primaryUpiId}</button>
            ) : null}
            {shop?.upi_qr_note ? <p className="text-center text-xs text-muted-foreground">{shop.upi_qr_note}</p> : null}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
